import { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  SvgIcon,
  Typography,
  Tooltip,
  Button,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Footer } from './Footer';
import { TBreadcrumbs } from '../../types/types';
import Breadcrumbs from './../../elements/Breadcrumbs';
import PostJobDialog from '../Job/PostJobDialog';
import { STORAGE_USER } from '../../constants/storage';
import { useAuth } from '../../hooks/useAuth';

interface LaoyutContextType {
  value?: any
  isServiceUpdated: boolean
  openPostJob: boolean
  openPostJobRebook: (id: string) => void
  requestService: () => void
}

const LaoyutContext = createContext<LaoyutContextType>(null!);

const humanizeSegment = (segment: string, role?: string) => {
  const map: Record<string, string> = {
    dashboard: role === 'ADMIN' ? 'Admin Dashboard' : 'Client Dashboard',
    settings: 'Settings',
    password: 'Password',
    questionnaires: 'Questionnaires',
    rates: 'Rates',
    questionnaire: 'Questionnaire',
  };
  if (map[segment]) {
    return map[segment];
  }
  return segment
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const LayoutDashboard = () => {
  const [user, setUser] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rebookId, setRebookId] = useState('');
  const [openPostJob, setOpenPostJob] = useState(false);
  const [links, setLinks] = useState<TBreadcrumbs[]>([]);
  const [isServiceUpdatedState, setIsServiceUpdatedState] = useState(false);
  const [showBreadcrumbs, setShowBreadcrumbs] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (localStorage && localStorage.getItem(STORAGE_USER)) {
      setUser(JSON.parse(localStorage.getItem(STORAGE_USER) || ''));
    }
  }, [location, location.pathname]);

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    let cumulative = '';
    const linksArr: TBreadcrumbs[] = segments.map((item) => {
      cumulative += `/${item}`;
      return {
        title: humanizeSegment(item, user?.role),
        url: cumulative,
      };
    });
    setLinks(linksArr);
    const isDashboardHome = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    setShowBreadcrumbs(
      !isDashboardHome && !location.pathname.includes('/dashboard/questionnaire')
    );
  }, [location.pathname, user?.role]);

  const handleClickOpenPostJobRebook = (id: string) => {
    setRebookId(id);
    setOpenPostJob(true);
    setIsServiceUpdatedState(false);
  };


  const handleClickOpen = () => {
    setRebookId('');
    setOpenPostJob(true);
    setIsServiceUpdatedState(false);
  };

  const handleClickClose = () => {
    setOpenPostJob(false);
    setIsServiceUpdatedState(true);
    setRebookId('');
  };

  return (
    <Box sx={[
      {
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '60px',
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        overflow: 'auto'
      },
      (theme) => ({
        [theme.breakpoints.down('sm')]: {
          paddingBottom: '92px',
        }
      }),
    ]}>
      <CssBaseline />
      <Box sx={{
        borderBottom: '1px solid #DDDFDD',
      }}>
        <Box component={'header'}>
          <Container maxWidth={false}>
            <Box sx={{
              py: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              maxWidth: '100%',
              width: '100%',
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                height: '40px',
                flexShrink: 0,
                '& a': {
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                },
                '& img': {
                  display: 'block',
                  height: '40px',
                  width: 'auto',
                },
              }}>
                <Tooltip title="Home">
                  <Link to={'/dashboard'}>
                    <img src="/lucky-logo.svg" alt="Lucky Services" />
                  </Link>
                </Tooltip>
              </Box>
              <Box sx={{
                ml: 'auto',
                minWidth: 0,
              }} component={'nav'}>
                <Box sx={[
                  {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    '& > a': {
                      display: 'flex',
                      alignItems: "center",
                      textDecoration: 'none',
                    }
                  },
                  (theme) => ({
                    [theme.breakpoints.up('sm')]: {
                      gap: '12px',
                    },
                  }),
                ]}>
                  {user?.role === "ADMIN" && !isMobile && (
                    <>
                      <Button
                        sx={{ textTransform: 'none' }}
                        onClick={() => navigate('/dashboard')}
                      >
                        Clients
                      </Button>
                      <Button
                        sx={{ textTransform: 'none' }}
                        onClick={() => navigate('/dashboard/questionnaires')}
                      >
                        Questionnaires
                      </Button>
                      <Button
                        sx={{ textTransform: 'none' }}
                        onClick={() => navigate('/dashboard/rates')}
                      >
                        Rates
                      </Button>
                    </>
                  )}
                  {user?.role === "CLIENT" && (
                    <Button
                      variant="lucky"
                      onClick={handleClickOpen}
                      sx={{
                        flexShrink: 0,
                        ...(isMobile && {
                          fontSize: '13px',
                          lineHeight: '18px',
                          py: '8px',
                          px: '12px',
                        }),
                      }}
                    >
                      {isMobile ? 'Book' : 'Request Service'}
                    </Button>
                  )}
                  {!isMobile && (
                    <Typography
                      sx={{
                        color: '#455445',
                        fontSize: '14px',
                        lineHeight: '20px',
                        maxWidth: '220px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user?.firstName || user?.email || 'Account'}
                    </Typography>
                  )}
                  <IconButton
                    aria-label="Account menu"
                    onClick={(event) => setProfileAnchor(event.currentTarget)}
                    sx={{
                      width: 40,
                      height: 40,
                      border: '1px solid #316E59',
                      color: '#316E59',
                      '&:hover': {
                        backgroundColor: 'rgba(49, 110, 89, 0.08)',
                      },
                    }}
                  >
                    <SvgIcon sx={{ fontSize: 20 }} viewBox="0 0 16 16">
                      <path d="M8.00125 8.3084C10.5548 8.3084 12.6422 6.43993 12.6422 4.1542C12.6422 1.86847 10.5548 0 8.00125 0C5.44771 0 3.36035 1.86847 3.36035 4.1542C3.36035 6.43993 5.44775 8.3084 8.00125 8.3084Z" fill="currentColor" />
                      <path d="M15.9658 11.628C15.8442 11.3559 15.6821 11.1019 15.4997 10.8661C14.5674 9.63253 13.1286 8.81621 11.5073 8.61667C11.3047 8.59854 11.0817 8.63479 10.9196 8.74364C10.0684 9.30601 9.05512 9.59625 8.00126 9.59625C6.94739 9.59625 5.93411 9.30601 5.08293 8.74364C4.92078 8.63479 4.69785 8.58038 4.49521 8.61667C2.87392 8.81621 1.41478 9.63253 0.502813 10.8661C0.32042 11.1019 0.158276 11.3741 0.0367065 11.628C-0.0240782 11.7369 -0.00382961 11.8639 0.0569551 11.9727C0.2191 12.2267 0.421741 12.4807 0.604134 12.6983C0.887848 13.043 1.19185 13.3514 1.53639 13.6416C1.8201 13.8956 2.14435 14.1314 2.46864 14.3673C4.06964 15.4376 5.99493 15.9999 7.98101 15.9999C9.96708 15.9999 11.8924 15.4375 13.4934 14.3673C13.8176 14.1496 14.1419 13.8956 14.4256 13.6416C14.7499 13.3514 15.0741 13.043 15.3579 12.6983C15.5605 12.4625 15.743 12.2267 15.9051 11.9727C16.0063 11.8639 16.0266 11.7368 15.9658 11.628Z" fill="currentColor" />
                    </SvgIcon>
                  </IconButton>
                  <Menu
                    anchorEl={profileAnchor}
                    open={Boolean(profileAnchor)}
                    onClose={() => setProfileAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <Box sx={{ px: '16px', py: '8px', maxWidth: '240px' }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px' }}>
                        {user?.firstName || 'Account'}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', lineHeight: '16px', color: '#455445', wordBreak: 'break-all' }}>
                        {user?.email}
                      </Typography>
                    </Box>
                    <Divider />
                    {user?.role === "ADMIN" && isMobile && (
                      <>
                        <MenuItem onClick={() => { setProfileAnchor(null); navigate('/dashboard'); }}>Clients</MenuItem>
                        <MenuItem onClick={() => { setProfileAnchor(null); navigate('/dashboard/questionnaires'); }}>Questionnaires</MenuItem>
                        <MenuItem onClick={() => { setProfileAnchor(null); navigate('/dashboard/rates'); }}>Rates</MenuItem>
                        <Divider />
                      </>
                    )}
                    <MenuItem onClick={() => { setProfileAnchor(null); navigate('/dashboard/settings'); }}>
                      Settings
                    </MenuItem>
                    <MenuItem onClick={() => { setProfileAnchor(null); logout(); }}>
                      Log out
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
              {user?.role === "CLIENT" && <PostJobDialog
                rebookId={rebookId}
                isOpen={openPostJob}
                handleClose={handleClickClose}
              />}
            </Box>
          </Container>
        </Box>
      </Box>
      <LaoyutContext.Provider value={{
        isServiceUpdated: isServiceUpdatedState,
        openPostJob: openPostJob,
        openPostJobRebook: handleClickOpenPostJobRebook,
        requestService: handleClickOpen,
      }}>
        <Box sx={{ display: 'flex', position: 'relative', flexGrow: 1, }}>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              maxWidth: '100%',
              width: '100%',
              display: 'flex',
              minHeight: '100%',
            }}>
            <Container
              maxWidth={false}
              sx={[
                { pt: '28px' },
                (theme) => ({
                  [theme.breakpoints.up('sm')]: {
                    pt: '16px',
                  },
                }),
              ]}
            >
              {showBreadcrumbs && links.filter((item) =>
                item.title !== 'Client Dashboard' && item.title !== 'Admin Dashboard'
              ).length > 0 && (
                <Box sx={{
                  marginTop: { xs: '0px', sm: '8px' },
                  marginBottom: { xs: '20px', sm: '32px' },
                }}>
                  <Breadcrumbs
                    links={links.filter((item) =>
                      item.title !== 'Client Dashboard' && item.title !== 'Admin Dashboard'
                    )}
                  />
                </Box>
              )}
              <Outlet />
            </Container>
          </Box>
        </Box>
      </LaoyutContext.Provider>
      <Footer />
    </Box >
  )
};

export const useLayout = () => {
  return useContext(LaoyutContext);
};

export default LayoutDashboard;
